/**
 * Unit coverage hooks (migration 0185) -- thin TanStack Query layer over
 * unitCoverageApi (src/lib/api.ts), mirroring the notifications.ts hook
 * convention.
 *
 * useUnitCoverage backs the Team directory's per-unit county line (every
 * view_staff_tools seat). useSetUnitCoverage backs the Admin > Units
 * county picker (manage_team) and invalidates the shared list query on
 * success so the directory and the picker never disagree. A coverage save
 * refreshes the tenant config too, because the tenant's working states
 * (where a new address's State starts) come from its unit coverage.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unitCoverageApi, type UnitCoverageRow } from '@/lib/api';
import { tenantConfigQueryKey } from '@/hooks/useTenantConfig';

export function unitCoverageQueryKey(slug: string): readonly unknown[] {
  return ['unit-coverage', slug];
}

export function useUnitCoverage(
  slug: string,
  opts: { enabled?: boolean } = {},
): { units: UnitCoverageRow[]; isLoading: boolean; isError: boolean; error: unknown } {
  const query = useQuery({
    queryKey: unitCoverageQueryKey(slug),
    queryFn: () => unitCoverageApi.list(slug),
    enabled: opts.enabled ?? true,
  });
  return { units: query.data?.units ?? [], isLoading: query.isLoading, isError: query.isError, error: query.error };
}

export function useSetUnitCoverage(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ unitId, areaIds }: { unitId: string; areaIds: string[] }) =>
      unitCoverageApi.setCoverage(slug, unitId, areaIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: unitCoverageQueryKey(slug) });
      void queryClient.invalidateQueries({ queryKey: tenantConfigQueryKey(slug) });
    },
  });
}
